import React from "react";
import {
  Alert,
  Badge,
  Button,
  ListGroup,
  ListGroupItem,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { helpArticleUrl } from "../../../constants/helpRouteMap";
import type {
  DeliverabilityBand,
  DeliverabilityCheckResult,
  DeliverabilityFinding,
} from "../../../helpers/deliverability_helper";

type Props = {
  result: DeliverabilityCheckResult | null;
  loading?: boolean;
  error?: string | null;
  onRecheck?: () => void;
  /** Em risk: CTA explícito de “enviar mesmo assim” (tela de disparo). */
  onSendAnyway?: () => void;
  compact?: boolean;
};

const bandColor = (band: DeliverabilityBand): string => {
  switch (band) {
    case "good":
      return "success";
    case "attention":
      return "warning";
    case "risk":
      return "danger";
    default:
      return "secondary";
  }
};

const severityIcon = (severity: DeliverabilityFinding["severity"]): string => {
  switch (severity) {
    case "critical":
      return "ri-error-warning-fill text-danger";
    case "warn":
      return "ri-alert-fill text-warning";
    default:
      return "ri-information-fill text-info";
  }
};

/**
 * Painel Pré-voo — faixa + findings leigos (sem code cru).
 * DOC 04 § 4.8.2 · 13 UX.
 */
const PreflightPanel = ({
  result,
  loading = false,
  error = null,
  onRecheck,
  onSendAnyway,
  compact = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const helpUrl = helpArticleUrl(
    "/sending/deliverability-basics",
    i18n.language
  );

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted py-2">
        <Spinner size="sm" /> {t("preflight.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger" className="mb-0">
        {error}
        {onRecheck ? (
          <div className="mt-2">
            <Button color="light" size="sm" type="button" onClick={onRecheck}>
              {t("preflight.retry")}
            </Button>
          </div>
        ) : null}
      </Alert>
    );
  }

  if (!result) {
    return null;
  }

  return (
    <div className={compact ? "" : "border rounded p-3 bg-light-subtle"}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Badge
          color={bandColor(result.band)}
          className="fs-6"
          title={t("preflight.disclaimer")}
        >
          {t(`preflight.band.${result.band}`)}
        </Badge>
        <span className="text-muted small">
          {t("preflight.scoreHint", { score: result.score })}
        </span>
        {onRecheck ? (
          <Button
            color="link"
            size="sm"
            className="p-0 ms-auto"
            type="button"
            onClick={onRecheck}
          >
            {t("preflight.recheck")}
          </Button>
        ) : null}
      </div>

      <Alert color="info" className="py-2 mb-3">
        <div className="small mb-1">{t("preflight.disclaimer")}</div>
        <a
          href={helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="small fw-semibold"
        >
          {t("preflight.helpLink")}
        </a>
      </Alert>

      {result.findings.length === 0 ? (
        <Alert color="success" className="mb-0 py-2">
          {t("preflight.noFindings")}
        </Alert>
      ) : (
        <ListGroup flush className="mb-0">
          {result.findings.map((f) => (
            <ListGroupItem
              key={f.code}
              className="px-0 bg-transparent border-0 py-2"
            >
              <div className="d-flex gap-2">
                <i className={`${severityIcon(f.severity)} fs-5 mt-0`} />
                <div>
                  <div className="fw-medium">{f.message}</div>
                  {f.hint ? (
                    <div className="text-muted small">{f.hint}</div>
                  ) : null}
                </div>
              </div>
            </ListGroupItem>
          ))}
        </ListGroup>
      )}

      {result.band === "risk" && onSendAnyway ? (
        <div className="mt-3">
          <Button color="warning" type="button" onClick={onSendAnyway}>
            {t("preflight.sendAnyway")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default PreflightPanel;
